import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  Grid,
  Fab,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  RateReview,
  StarOutline,
  PeopleOutline,
  TodayOutlined,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ReviewCard = ({
  review,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      mb: 1.25,
      border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 1.25, py: 0.875 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "24px 1fr auto auto",
          alignItems: "center",
          columnGap: 0.5,
          mb: 0.35,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(review)}
          sx={{
            p: 0,
            color: BRAND.muted,
            "&.Mui-checked": { color: ORANGE },
          }}
          inputProps={{ "aria-label": `Select ${review.employee_name}` }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: NAVY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {review.employee_name || "Unknown"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onEdit(review)}
          aria-label="Edit review"
          sx={{ p: 0, color: ORANGE, width: 32, height: 32 }}
        >
          <EditIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(review.id)}
          aria-label="Delete review"
          sx={{ p: 0, color: "#d32f2f", width: 32, height: 32 }}
        >
          <DeleteIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
      </Box>

      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: ORANGE,
          pl: "calc(24px + 6px)",
          mb: 0.35,
        }}
      >
        {review.employee_id || "N/A"}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.6875rem",
          color: BRAND.muted,
          pl: "calc(24px + 6px)",
          mb: 0.5,
        }}
      >
        {review.appraisal_period || "No period"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          pl: "calc(24px + 6px)",
        }}
      >
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
          Rating:{" "}
          <strong style={{ color: NAVY }}>{review.rating ?? "—"}</strong>
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.border }}>
          ·
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
          Date:{" "}
          <strong style={{ color: NAVY }}>{review.review_date || "—"}</strong>
        </Typography>
      </Box>
    </Box>
  </Box>
);

const AppraisalReviewMobileView = ({
  reviews,
  stats,
  searchTerm,
  setSearchTerm,
  selectedRows,
  onToggleSelect,
  onEdit,
  onDelete,
  onAdd,
  onExport,
  onClearSelection,
}) => {
  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => row.id)),
    [selectedRows]
  );

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        Appraisal Review
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        Record and manage reviewer ratings for appraisal cycles
      </Typography>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<RateReview sx={{ fontSize: "1rem" }} />}
            label="Total Reviews"
            value={stats.total}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
        </Grid>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<StarOutline sx={{ fontSize: "1rem" }} />}
            label="Avg Rating"
            value={stats.avgRating}
            iconBg="rgba(0,188,212,0.15)"
            iconColor="#00bcd4"
          />
        </Grid>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<PeopleOutline sx={{ fontSize: "1rem" }} />}
            label="Employees Reviewed"
            value={stats.uniqueEmployees}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
          />
        </Grid>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<TodayOutlined sx={{ fontSize: "1rem" }} />}
            label="Reviews Today"
            value={stats.todayCount}
            iconBg="rgba(244,67,54,0.12)"
            iconColor="#f44336"
          />
        </Grid>
      </Grid>

      {selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            bgcolor: BRAND.orangeLight,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY, mb: 1 }}>
            {selectedRows.length} selected — export or clear
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={onExport}
              sx={{
                flex: 1,
                border: "none",
                borderRadius: "10px",
                bgcolor: ORANGE,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <ExportIcon sx={{ fontSize: "1rem" }} />
              Export
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClearSelection}
              sx={{
                flex: 1,
                border: `1px solid ${BRAND.border}`,
                borderRadius: "10px",
                bgcolor: "#fff",
                color: NAVY,
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
              }}
            >
              Clear
            </Box>
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder="Search reviews..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {reviews.length > 0 ? (
        reviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            isSelected={selectedIds.has(review.id)}
            onToggleSelect={onToggleSelect}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <RateReview sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No appraisal reviews yet
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            Tap + to submit the first review.
          </Typography>
        </Box>
      )}

      <Fab
        color="primary"
        aria-label="Add appraisal review"
        onClick={onAdd}
        size="medium"
        sx={{
          position: "fixed",
          bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
          right: 20,
          bgcolor: ORANGE,
          "&:hover": { bgcolor: BRAND.orange },
          boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
          zIndex: 1002,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default AppraisalReviewMobileView;
